/**
 * Plan feature utilities — parallel to permissionsUtils.js.
 *
 * - **Company users** — access and quotas come from the active subscription plan.
 * - System Admin has no plan-feature bypass in the tenant app.
 *
 * Prefer `usePlanFeatureAccess()` in components instead of ad-hoc limit checks.
 * Backend `checkFeatureLimit` middleware remains the source of truth for enforcement.
 */

import { USER_ROLES, isSystemAdminRole } from './permissionsUtils';

/**
 * Canonical feature keys stored in the Feature collection (featureUniqueKey).
 */
export const FEATURE_KEYS = {
	CONTACTS: 'contacts_allowed_solo',
	EMPLOYEES: 'maximum_Number_employees',
	CLIENTS: 'clients_management',
	PROJECTS: 'maximum_project_creation',
	PROJECT_ANALYTICS: 'project_analytics_reporting',
	CAMPAIGN_CREATION: 'monthly_campaign_creation__management',
	EMAIL_CAMPAIGN_SENT: 'monthly_email_campaign_sent',
	EMAIL_BUILDER: 'dragndrop_email_builder',
	PREMIUM_TEMPLATES: 'free_premium_email_templates',
	BRANDED_WORKSPACE: 'branded_workspace',
	CLIENT_PORTAL: 'client_portal',
	FINANCIAL_ANALYTICS: 'financial_reporting_analytics',
	CRM_LEADS: 'crm_features_todos__leads_management',
	AI_WORDPRESS_CONTENT: 'aipowered_wordpress_content_generation',
	AI_AUTOMATION_GENERATION: 'aipowered_automation_generation',
	AI_MARKETING_ASSIST: 'aipowered_marketing_assist',
	BRANDED_INVOICES_AND_DIFFERENT_LAYOUTS: 'branded_invoices_and_different_layouts',
};

/** Legacy or mismatched keys mapped to canonical FEATURE_KEYS values. */
export const FEATURE_KEY_ALIASES = {
	employees_management: FEATURE_KEYS.EMPLOYEES,
};

export const FEATURE_TIER_VALUES = new Set(['basic', 'detailed', 'extended']);

/**
 * @typedef {Object} FeatureAccessOptions
 * @property {boolean} [isSystemAdmin] Kept for redirect detection; does not grant access
 * @property {string} [userRole]
 */

export const normalizeFeatureAccessOptions = (options = {}) => {
	const userRole = options.userRole;
	return {
		isSystemAdmin: options.isSystemAdmin ?? isSystemAdminRole(userRole),
		userRole,
	};
};

/** Tenant app never elevates System Admin past plan limits. */
export const hasElevatedFeatureAccess = () => false;

export const normalizeFeatureKey = (featureKey) =>
	FEATURE_KEY_ALIASES[featureKey] || featureKey;

/** Default when a feature is not on the company's plan limits map. */
export const EMPTY_FEATURE_RECORD = {
	name: null,
	description: null,
	readableLimit: null,
	displayLimit: null,
	rawLimit: null,
	numericLimit: null,
	isUnlimited: false,
	isTier: false,
	isAccessible: false,
	isActive: true,
	isEnforcementActive: true,
};

export const isFeatureEnforcementActive = (featureRecord) =>
	featureRecord?.isEnforcementActive !== false;

/** True when a feature should appear in plan comparison / matrix tables. */
export const isFeatureVisibleOnPlanTable = (feature) =>
	feature?.displayOnPlanTable !== false;

/** Unique feature names across plans, excluding hidden matrix rows. */
export const collectVisiblePlanMatrixFeatureNames = (plans = []) =>
	Array.from(
		new Set(
			(Array.isArray(plans) ? plans : []).flatMap((plan) =>
				(plan.features || [])
					.filter(isFeatureVisibleOnPlanTable)
					.map((f) => f?.featureName || f?.name)
					.filter(Boolean),
			),
		),
	);

/** True when a plan limit value means the feature is included (show check icon). */
export const isIncludedFeatureLimit = (value) =>
	String(value ?? '').trim().toLowerCase() === 'included';

/** True when the plan matrix should render a green check instead of limit text. */
export const shouldShowPlanMatrixCheckIcon = (value) =>
	value === undefined ||
	value === null ||
	value === '' ||
	isIncludedFeatureLimit(value);

/**
 * Parse a raw limit value from the Feature collection into a normalized shape.
 *
 * @param {string|number|null|undefined} rawLimit
 * @returns {{
 *   rawLimit: string|number|null,
 *   numericLimit: number|string|null,
 *   isUnlimited: boolean,
 *   isTier: boolean,
 *   isAccessible: boolean,
 * }}
 */
export const parseFeatureLimit = (rawLimit) => {
	if (rawLimit === null || rawLimit === undefined || rawLimit === '') {
		return {
			rawLimit: null,
			numericLimit: null,
			isUnlimited: false,
			isTier: false,
			isAccessible: false,
		};
	}

	const lower = String(rawLimit).toLowerCase();
	if (lower.includes('unlimited') || lower.includes('bespoke')) {
		return {
			rawLimit,
			numericLimit: null,
			isUnlimited: true,
			isTier: false,
			isAccessible: true,
		};
	}

	if (FEATURE_TIER_VALUES.has(lower)) {
		return {
			rawLimit,
			numericLimit: rawLimit,
			isUnlimited: false,
			isTier: true,
			isAccessible: true,
		};
	}

	const match = String(rawLimit).match(/\d+/);
	const numericLimit = match ? parseInt(match[0], 10) : null;

	return {
		rawLimit,
		numericLimit,
		isUnlimited: false,
		isTier: false,
		isAccessible: true,
	};
};

/**
 * Build a normalized feature record from the limits API row.
 * @param {Object|null|undefined} apiRow
 */
export const normalizeFeatureRecord = (apiRow) => {
	if (!apiRow) {
		return { ...EMPTY_FEATURE_RECORD };
	}

	const isEnforcementActive = apiRow.isActive !== false;
	const parsed = parseFeatureLimit(apiRow.limit);

	return {
		name: apiRow.name ?? null,
		description: apiRow.description ?? null,
		readableLimit: apiRow.readableLimit ?? null,
		displayLimit: apiRow.displayLimit ?? apiRow.readableLimit ?? apiRow.limit ?? null,
		isActive: isEnforcementActive,
		isEnforcementActive,
		...parsed,
	};
};

/**
 * True when the feature is included on the company's plan.
 */
export const canUseFeature = (featureRecord, accessOptions = null) => {
	if (accessOptions && hasElevatedFeatureAccess(accessOptions)) {
		return true;
	}
	if (featureRecord?.isEnforcementActive === false) {
		return true;
	}
	return Boolean(featureRecord?.isAccessible);
};

/**
 * True when a count-based feature has no remaining quota.
 * Tier and access-only features are never "at limit" via usage count.
 */
export const isAtFeatureLimit = (currentUsage, featureRecord, accessOptions = null) => {
	if (accessOptions && hasElevatedFeatureAccess(accessOptions)) {
		return false;
	}
	if (featureRecord?.isEnforcementActive === false) {
		return false;
	}
	if (!featureRecord?.isAccessible) return true;
	if (featureRecord.isUnlimited || featureRecord.isTier) return false;
	if (featureRecord.numericLimit === null || typeof featureRecord.numericLimit !== 'number') {
		return false;
	}
	return Number(currentUsage) >= featureRecord.numericLimit;
};

export const hasRemainingQuota = (currentUsage, featureRecord, accessOptions = null) =>
	!isAtFeatureLimit(currentUsage, featureRecord, accessOptions);

export const getUsageRatio = (currentUsage, featureRecord) => {
	if (featureRecord?.isEnforcementActive === false) {
		return 0;
	}
	if (!featureRecord?.isAccessible || featureRecord.isUnlimited || featureRecord.isTier) {
		return 0;
	}
	if (typeof featureRecord.numericLimit !== 'number' || featureRecord.numericLimit <= 0) {
		return 0;
	}
	return Math.min(Number(currentUsage) / featureRecord.numericLimit, 1);
};

/**
 * @param {Record<string, Object>} limitsMap  featureUniqueKey → normalized record
 * @param {FeatureAccessOptions} [accessOptions]
 */
export const createFeatureChecker = (limitsMap = {}, accessOptions = {}) => {
	const resolve = (featureKey) => {
		const key = normalizeFeatureKey(featureKey);
		return limitsMap[key] ?? null;
	};

	return {
		canUse: (featureKey) => canUseFeature(resolve(featureKey), accessOptions),
		getFeature: (featureKey) => resolve(featureKey) ?? { ...EMPTY_FEATURE_RECORD },
		isEnforcementActive: (featureKey) =>
			isFeatureEnforcementActive(resolve(featureKey)),
		isAtLimit: (featureKey, currentUsage = 0) =>
			isAtFeatureLimit(currentUsage, resolve(featureKey), accessOptions),
		hasQuota: (featureKey, currentUsage = 0) =>
			hasRemainingQuota(currentUsage, resolve(featureKey), accessOptions),
		getUsageRatio: (featureKey, currentUsage = 0) =>
			getUsageRatio(currentUsage, resolve(featureKey)),
	};
};

/**
 * Human-readable summary for feature restriction alerts.
 * @param {Array<{ subject: string, reason: 'unavailable'|'limit', limit?: number, usage?: number }>} restrictions
 */
export const buildFeatureRestrictionMessage = (restrictions) => {
	if (!restrictions.length) return '';

	const parts = restrictions.map(({ subject, reason, limit, usage }) => {
		if (reason === 'unavailable') {
			return `${subject} is not included on your current plan`;
		}
		return `${subject} limit reached (${usage ?? 0} of ${limit})`;
	});

	return `${parts.join('; ')}. Upgrade your plan for more access.`;
};

export { USER_ROLES };
